// Two lists and the arrows between them — the shape the old Windows admin form
// used for every mapping, and the reason it was quick to work with: what a user
// HAS and what they COULD have are on screen together, so granting access never
// means remembering what was in a dropdown.
//
// Deliberately kept dumb. It owns selection and nothing else; the caller owns
// the data and every write, because each of the four levels writes to a
// different table and half of them cascade.
//
// ⚠️ THE RIGHT-HAND ORDER IS DATA, NOT DECORATION. It is the SortOrder the app
// draws locations, rooms and devices in, so the arrows are a write, not a view
// preference — see core/admin/userAccess.reorder*.
//
// ── It FILLS its parent, it does not size itself ───────────────────────────
// No fixed list height anywhere. Four of these stack on one page and all four
// have to be visible at once, so each takes the share of the column it is given
// and scrolls INSIDE its own list. A min/max height here would push the last
// panel off the bottom on a laptop, which is the whole problem this page had.
//
// Every ancestor of a scrolling list needs `minHeight: 0`. A flex child's
// default min-height is auto — it refuses to shrink below its content — so
// without it the list grows to fit all 110 locations and the page scrolls
// instead of the list.

import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, List, ListItemButton, ListItemText, IconButton,
  Divider, CircularProgress, Tooltip, InputBase,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { matchesTokens } from '../../core/utils/search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Each pane searches ITSELF. One box over both lists would be ambiguous the
// moment the two contain a similar name, and the question here is usually
// one-sided — "is this location already allotted" is asked of the right list,
// "where is it" of the left.
//
// Matching is core/utils/search.matchesTokens, the same rule as every picker in
// the app: word order does not matter, and a unique number matches on any
// substring so "4811" finds 884811.
function Pane({
  title, items, keyOf, labelOf, subLabelOf, selected, onSelect, onActivate, empty,
  query, onQuery,
}) {
  const shown = useMemo(
    () => items.filter((it) => matchesTokens(
      `${labelOf(it)} ${subLabelOf ? subLabelOf(it) : ''}`,
      query,
    )),
    [items, query, labelOf, subLabelOf],
  );

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Title, count and search share one line — a search box on its own row
          would cost every panel the two list rows it is meant to save. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.125,
        bgcolor: 'action.hover', flexShrink: 0, minWidth: 0,
      }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 10.5, lineHeight: 1.5, flexShrink: 0 }}>
          {/* Both counts when filtering, so a short list never reads as a short
              list when it is really a filtered one. */}
          {query ? `${title} (${shown.length}/${items.length})` : `${title} (${items.length})`}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <SearchIcon sx={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }} />
        <InputBase
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="search"
          sx={{
            fontSize: 10.5,
            width: 64,
            flexShrink: 1,
            minWidth: 0,
            '& input': { p: 0 },
          }}
        />
      </Box>
      <Divider />
      {/* The only thing on the page allowed to scroll. */}
      {/* `scrollbarGutter: stable` keeps the two panes the SAME width whether or
          not one of them is scrolling. Without it the left list, which is
          usually the long one, is a scrollbar narrower than the right and the
          two never line up. */}
      <List
        dense
        disablePadding
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}
      >
        {shown.length === 0 && (
          <Typography
            color="text.secondary"
            sx={{ display: 'block', px: 0.75, py: 0.5, fontSize: 11 }}
          >
            {items.length ? 'Nothing matches that search.' : empty}
          </Typography>
        )}
        {shown.map((it) => {
          const k = String(keyOf(it));
          const sub = subLabelOf ? subLabelOf(it) : null;
          return (
            <ListItemButton
              key={k}
              selected={selected === k}
              onClick={() => onSelect(k)}
              // Double-click moves it — the old form's habit, and faster than
              // travelling to the arrow for every single row.
              onDoubleClick={onActivate}
              sx={{ py: 0, px: 0.75, minHeight: 0 }}
            >
              {/* Name and id on ONE line rather than two: it halves the row
                  height, and the id is a reference you glance at, not read. */}
              <ListItemText
                primary={labelOf(it)}
                primaryTypographyProps={{ noWrap: true, fontSize: 12 }}
                sx={{ my: 0.125 }}
              />
              {sub && (
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 10, ml: 0.75, flexShrink: 0 }}
                >
                  {sub}
                </Typography>
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
}

export default function TransferList({
  // The grid area this panel occupies, when the caller lays them out by name.
  area,
  title,
  note,
  availableTitle = 'Available',
  assignedTitle = 'Allotted',
  available = [],
  assigned = [],
  keyOf,
  labelOf,
  subLabelOf,
  onAllot,
  onUnallot,
  onReorder,
  onPick,
  picked,
  busy = false,
  disabled = false,
  emptyAvailable = 'Nothing left to allot.',
  emptyAssigned = 'Nothing allotted yet.',
}) {
  const [leftSel, setLeftSel] = useState(null);
  const [rightSel, setRightSel] = useState(null);
  const [leftQ, setLeftQ] = useState('');
  const [rightQ, setRightQ] = useState('');

  const find = (list, k) => list.find((x) => String(keyOf(x)) === String(k)) || null;

  const allot = () => {
    const it = find(available, leftSel);
    if (it && onAllot) { onAllot(it); setLeftSel(null); }
  };
  const unallot = () => {
    const it = find(assigned, rightSel);
    if (it && onUnallot) { onUnallot(it); setRightSel(null); }
  };

  // ⚠️ REORDER IS DISABLED WHILE THE ALLOTTED PANE IS FILTERED. The arrows swap
  // a row with its TRUE neighbour, which a search may have hidden — so on a
  // filtered list the row appears to jump over something that is not there, and
  // the SortOrder written is not the one the operator was looking at.
  const reorderable = !rightQ;

  // Reorder hands back the WHOLE list, because SortOrder is positional: moving
  // one row renumbers every row after it, and sending a single row's new index
  // would leave the rest claiming positions they no longer hold.
  const move = (delta) => {
    const i = assigned.findIndex((x) => String(keyOf(x)) === String(rightSel));
    const j = i + delta;
    if (i < 0 || j < 0 || j >= assigned.length || !onReorder) return;
    const next = [...assigned];
    [next[i], next[j]] = [next[j], next[i]];
    onReorder(next);
  };

  // Selecting on the right ALSO drills down — picking a location is how the
  // rooms panel below knows which location to show. One click, not two.
  const selectRight = (k) => {
    setRightSel(k);
    if (onPick) onPick(find(assigned, k));
  };

  const arrow = (icon, onClick, enabled, tip) => (
    <Tooltip title={tip}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={!enabled || disabled || busy}
          sx={{ p: 0.25 }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const pickedKey = picked == null ? null : String(keyOf(picked));

  return (
    <Paper
      variant="outlined"
      sx={{
        gridArea: area,
        p: 0.625,
        opacity: disabled ? 0.5 : 1,
        // Takes the share of the column it is given, rather than asking for a
        // height of its own — see the note at the top.
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* The note is an ICON, not a line of text. Sharing the row with the
          title meant both truncated on a narrow panel — "Rooms in Sanj…" beside
          "Order here is shared by …" — so neither could be read. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.375, flexShrink: 0 }}>
        <Typography
          sx={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.2, minWidth: 0 }}
          noWrap
        >
          {title}
        </Typography>
        {busy && <CircularProgress size={11} />}
        <Box sx={{ flex: 1 }} />
        {note && (
          <Tooltip title={note}>
            <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.55, flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', gap: 0.375 }}>
        <Pane
          title={availableTitle}
          items={available}
          keyOf={keyOf}
          labelOf={labelOf}
          subLabelOf={subLabelOf}
          selected={leftSel}
          onSelect={setLeftSel}
          onActivate={allot}
          empty={emptyAvailable}
          query={leftQ}
          onQuery={setLeftQ}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {arrow(<ChevronRightIcon />, allot, !!leftSel, 'Allot to this user')}
          {arrow(<ChevronLeftIcon />, unallot, !!rightSel, 'Take away from this user')}
        </Box>

        <Pane
          title={assignedTitle}
          items={assigned}
          keyOf={keyOf}
          labelOf={labelOf}
          subLabelOf={subLabelOf}
          selected={pickedKey ?? rightSel}
          onSelect={selectRight}
          onActivate={unallot}
          empty={emptyAssigned}
          query={rightQ}
          onQuery={setRightQ}
        />

        {onReorder && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {arrow(<ArrowUpwardIcon />, () => move(-1), !!rightSel && reorderable,
              reorderable ? 'Move up' : 'Clear the search to reorder')}
            {arrow(<ArrowDownwardIcon />, () => move(1), !!rightSel && reorderable,
              reorderable ? 'Move down' : 'Clear the search to reorder')}
          </Box>
        )}
      </Box>
    </Paper>
  );
}
